/**
 * Convert a whole-rupee integer into Indian-numbering-system English words.
 *
 * Examples:
 *   100     → "One Hundred"
 *   1000    → "One Thousand"
 *   100000  → "One Lakh"
 *   125000  → "One Lakh Twenty-Five Thousand"
 *   10000000 → "One Crore"
 *
 * The output is deliberately capitalised in Title Case since it's rendered as a helper
 * hint below currency inputs (per Money Gurukul UX — helps catch a stray zero when
 * typing a sum-assured).
 */

const ONES = [
  "",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen",
];

const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

function twoDigits(n) {
  if (n < 20) return ONES[n];
  const t = Math.floor(n / 10);
  const o = n % 10;
  return o ? `${TENS[t]}-${ONES[o]}` : TENS[t];
}

function threeDigits(n) {
  const h = Math.floor(n / 100);
  const rest = n % 100;
  let out = "";
  if (h) out += `${ONES[h]} Hundred`;
  if (rest) out += `${h ? " " : ""}${twoDigits(rest)}`;
  return out;
}

// Handles numbers up to 99 crore natively; larger amounts recurse via `two/threeDigits` so
// values like `1,20,00,00,000` render as "One Hundred Twenty Crore".
function bigDigits(n) {
  return n < 100 ? twoDigits(n) : threeDigits(n);
}

export function numberToIndianWords(input) {
  const num = Number(input);
  if (!Number.isFinite(num)) return "";
  if (num === 0) return "Zero";

  const n = Math.floor(Math.abs(num));
  if (n === 0) return "Zero";

  const parts = [];
  const crore = Math.floor(n / 10000000);
  const lakh = Math.floor((n % 10000000) / 100000);
  const thousand = Math.floor((n % 100000) / 1000);
  const rest = n % 1000;

  if (crore) parts.push(`${bigDigits(crore)} Crore`);
  if (lakh) parts.push(`${twoDigits(lakh)} Lakh`);
  if (thousand) parts.push(`${twoDigits(thousand)} Thousand`);
  if (rest) parts.push(threeDigits(rest));

  return (num < 0 ? "Minus " : "") + parts.join(" ");
}
